import type { FormEvent } from 'react'
import { useCallback } from 'react'
import { useMutation } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'

import { move } from '@jotta/grpc-connect-openapi/fileQuery'
import { showToast } from '@jotta/ui/ToastProvider'
import { renamePath } from '@jotta/utils/pathUtils'

import type { PathItemStore } from '../PathItemStore'
import { validatePath } from '../validatePath'

export const useRenamePathSubmitHandler = (
  store: PathItemStore,
  newName: string,
) => {
  const item = store.model
  const parent = store.folder

  const validatePathErrorMessage = validatePath(newName)
  const mutationErrorMessage = t`Rename failed. Please check that the name is valid and try again`
  const path = item.path
  const toPath = renamePath(path, newName)
  const onCancel = useCallback(() => {
    item.reset()
    parent.actions.cancelActiveAction()
  }, [item, parent])
  const { mutate, variables, isError } = useMutation(move, {
    throwOnError: false,
    onSuccess: () => {
      parent.removeItem(item.path)
      item.path = toPath
      item.name = newName
      item.submit()
      parent.addEntry(store)
      onCancel()
    },
    onError: () =>
      showToast({
        message: mutationErrorMessage,
        hasCloseButton: true,
      }),
  })

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      mutate({
        fromPath: path,
        toPath: toPath,
      })
    },
    [mutate, path, toPath],
  )

  return {
    errorMessage:
      validatePathErrorMessage ||
      (isError && variables?.toPath === toPath && mutationErrorMessage),
    onSubmit,
    onCancel,
  }
}
